import { useEffect, useState, useCallback } from "react";
import { adminCoins } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Trophy, Search, RefreshCw, Send } from "lucide-react";

const inputCls = "w-full px-3 py-2 rounded-lg bg-black/50 border border-white/10 focus:border-[#FFBF00]/50 focus:outline-none text-sm";
const btnGold = "px-4 py-2 rounded-lg bg-[#FFBF00] text-black font-semibold text-sm blocky-shadow-black hover-blocky inline-flex items-center gap-2 disabled:opacity-50";

const REWARD_TYPES = [
  { v: "eventpoint", l: "🏆 Event Point" },
  { v: "signup_bonus", l: "🎉 Signup Bonus" },
  { v: "giveaway", l: "🎁 Giveaway" },
  { v: "manual", l: "✍ Manual Custom" },
];

function fmtDate(s) {
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

/* ============ Admin Rewards (multi-select players) ============ */
export function AdminRewards() {
  const [players, setPlayers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [type, setType] = useState("eventpoint");
  const [customType, setCustomType] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = useCallback(() => {
    adminCoins.users(q).then(setPlayers).catch(() => {});
  }, [q]);
  const loadHistory = useCallback(() => {
    adminCoins.rewardsHistory().then(setHistory).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const selectAll = () => setSelected(new Set(players.map((p) => p.lostdc_id).filter(Boolean)));
  const unselectAll = () => setSelected(new Set());

  const submit = async (e) => {
    e.preventDefault();
    const finalType = type === "manual" ? customType.trim() : type;
    const amt = parseInt(amount, 10);
    if (!selected.size) return toast.error("Select at least one player.");
    if (!finalType) return toast.error("Choose or enter a reward type.");
    if (!amt || amt <= 0) return toast.error("Enter a valid amount.");
    setBusy(true);
    try {
      const res = await adminCoins.sendRewards({ users: [...selected], amount: amt, type: finalType });
      toast.success(`Reward sent to ${res.sent} player(s)${res.skipped?.length ? `, ${res.skipped.length} skipped` : ""}.`);
      setSelected(new Set()); setAmount(""); setCustomType("");
      load(); loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send reward.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-rewards-panel">
      <div className="glass rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="text-[#FFBF00]" size={20} />
          <h3 className="font-display text-2xl">Reward Center</h3>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <button type="button" onClick={selectAll} data-testid="rewards-select-all" className="px-3 py-1.5 rounded-lg bg-white/10 text-xs">Select All</button>
          <button type="button" onClick={unselectAll} data-testid="rewards-unselect-all" className="px-3 py-1.5 rounded-lg bg-white/10 text-xs">Unselect All</button>
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-2.5 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search player (LostDC ID)…" data-testid="rewards-search" className={inputCls + " pl-9"} />
          </div>
          <span className="text-xs text-white/50 self-center" data-testid="rewards-selected-count">{selected.size} selected</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-72 overflow-y-auto p-1 mb-4" data-testid="rewards-player-list">
          {players.map((p) => (
            <label key={p.user_id} data-testid={`rewards-player-${p.lostdc_id}`}
                   className={`flex flex-col gap-1 p-3 rounded-xl cursor-pointer border ${selected.has(p.lostdc_id) ? "border-[#FFBF00] bg-[#FFBF00]/10" : "border-white/10 bg-black/40"}`}>
              <span className="flex items-center gap-2">
                <input type="checkbox" checked={selected.has(p.lostdc_id)} onChange={() => toggle(p.lostdc_id)} className="accent-[#FFBF00]" disabled={!p.lostdc_id} />
                <span className="font-semibold text-sm truncate">{p.lostdc_id || "(no lostdc id)"}</span>
              </span>
              <span className="text-[11px] text-[#FFBF00]">{p.dc_coin} DC</span>
            </label>
          ))}
          {!players.length && <div className="col-span-full text-white/40 text-sm p-4">No players found.</div>}
        </div>

        <form onSubmit={submit} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="text-xs text-white/50">Reward Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="rewards-type" className={inputCls}>
              {REWARD_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          {type === "manual" && (
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-white/50">Custom Type</label>
              <input value={customType} onChange={(e) => setCustomType(e.target.value)} placeholder="Enter custom type" data-testid="rewards-custom-type" className={inputCls} />
            </div>
          )}
          <div className="w-32">
            <label className="text-xs text-white/50">Amount (DC)</label>
            <input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" data-testid="rewards-amount" className={inputCls} />
          </div>
          <button type="submit" disabled={busy} data-testid="rewards-send" className={btnGold}><Send size={14} /> Send Reward</button>
        </form>
      </div>

      <div className="glass rounded-2xl p-6" data-testid="rewards-history">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-xl">Reward History</h4>
          <button onClick={loadHistory} className="text-white/50 hover:text-white"><RefreshCw size={16} /></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-white/50 text-xs uppercase text-left">
              <th className="py-2">Receiver</th><th>Type</th><th>Amount</th><th>Date</th>
            </tr></thead>
            <tbody>
              {history.map((t) => (
                <tr key={t.id} className="border-t border-white/5" data-testid={`reward-row-${t.id}`}>
                  <td className="py-2">{t.receiver_lostdc}</td>
                  <td>🎁 {t.note}</td>
                  <td className="text-[#FFBF00]">{t.amount} DC</td>
                  <td className="text-white/50">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
              {!history.length && <tr><td colSpan={4} className="py-4 text-white/40">No rewards sent yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ============ Admin Giveaway (fund creators' pools) ============ */
export function AdminGiveaway() {
  const [creators, setCreators] = useState([]);
  const [q, setQ] = useState("");
  const [amounts, setAmounts] = useState({});
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState("");
  const [history, setHistory] = useState([]);

  const load = useCallback(() => { adminCoins.giveawayCreators(q).then(setCreators).catch(() => {}); }, [q]);
  const loadHistory = useCallback(() => { adminCoins.giveawayHistory().then(setHistory).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const grant = async (c) => {
    const amt = parseInt(amounts[c.lostdc_id], 10);
    if (!c.lostdc_id) return toast.error("This creator has not linked their Minecraft account yet.");
    if (!amt || amt <= 0) return toast.error("Enter a valid amount.");
    setBusy(c.lostdc_id);
    try {
      await adminCoins.giveawayGrant({ recipient: c.lostdc_id, amount: amt, note: notes[c.lostdc_id] || "" });
      toast.success(`Granted ${amt} DC to ${c.lostdc_id}'s giveaway pool.`);
      setAmounts((a) => ({ ...a, [c.lostdc_id]: "" })); setNotes((n) => ({ ...n, [c.lostdc_id]: "" }));
      load(); loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to grant.");
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-giveaway-panel">
      <div className="glass rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Gift className="text-[#FFBF00]" size={20} />
          <h3 className="font-display text-2xl">Creator Giveaway Pools</h3>
        </div>
        <p className="text-white/50 text-sm mb-4">Fund a creator's giveaway pool. Creators can gift these coins to players (with a PIN + reason) but can never spend them on themselves.</p>

        <div className="relative mb-4 max-w-sm">
          <Search size={14} className="absolute left-3 top-2.5 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search creator…" data-testid="giveaway-search" className={inputCls + " pl-9"} />
        </div>

        <div className="space-y-3" data-testid="giveaway-creator-list">
          {creators.map((c) => (
            <div key={c.email} data-testid={`giveaway-creator-${c.lostdc_id || c.email}`}
                 className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-black/40 border border-white/10">
              <img src={c.avatar} alt="" className="w-9 h-9 rounded-full" />
              <div className="min-w-[140px]">
                <div className="font-semibold text-sm">{c.lostdc_id || c.name}</div>
                <div className="text-[11px] text-white/40">{c.email}</div>
              </div>
              <div className="text-xs">Pool: <span className="text-[#FFBF00] font-semibold" data-testid={`giveaway-pool-${c.lostdc_id}`}>{c.dc_giveaway} DC</span></div>
              <input type="number" min={1} placeholder="Amount" value={amounts[c.lostdc_id] || ""}
                     onChange={(e) => setAmounts((a) => ({ ...a, [c.lostdc_id]: e.target.value }))}
                     data-testid={`giveaway-amount-${c.lostdc_id}`} className={inputCls + " w-28"} />
              <input placeholder="Note (optional)" value={notes[c.lostdc_id] || ""}
                     onChange={(e) => setNotes((n) => ({ ...n, [c.lostdc_id]: e.target.value }))}
                     className={inputCls + " flex-1 min-w-[140px]"} />
              <button onClick={() => grant(c)} disabled={busy === c.lostdc_id} data-testid={`giveaway-grant-${c.lostdc_id}`} className={btnGold}>
                <Gift size={14} /> Fund
              </button>
            </div>
          ))}
          {!creators.length && <div className="text-white/40 text-sm p-4">No creators found. Mark users as creators in the Creators tab.</div>}
        </div>
      </div>

      <div className="glass rounded-2xl p-6" data-testid="giveaway-history">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-xl">Giveaway Activity</h4>
          <button onClick={loadHistory} className="text-white/50 hover:text-white"><RefreshCw size={16} /></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-white/50 text-xs uppercase text-left">
              <th className="py-2">Type</th><th>From</th><th>To</th><th>Amount</th><th>Reason / Note</th><th>Date</th>
            </tr></thead>
            <tbody>
              {history.map((t) => (
                <tr key={t.id} className="border-t border-white/5" data-testid={`giveaway-row-${t.id}`}>
                  <td className="py-2">{t.type === "giveaway_grant" ? "🏦 Grant" : "🎁 Gift"}</td>
                  <td>{t.type === "giveaway_grant" ? "Admin" : t.sender_lostdc}</td>
                  <td>{t.receiver_lostdc}</td>
                  <td className="text-[#FFBF00]">{t.amount} DC</td>
                  <td className="text-white/70">{t.note}</td>
                  <td className="text-white/50">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
              {!history.length && <tr><td colSpan={6} className="py-4 text-white/40">No giveaway activity yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
